/**
 * Sprint 14.1b — Duplicate-email guard modal.
 *
 * Triggered when the user enters an email on the New Candidate wizard
 * (or in bulk Resume Enrichment) that matches an existing candidate.
 *
 * Offers two choices:
 *   1. "Start a new evaluation for this candidate"
 *      → navigate to /portal/candidates/{matched_id}?action=new-eval
 *   2. "Open existing active evaluation"
 *      → navigate to /portal/candidates/{matched_id}
 *      (disabled when there's no active evaluation on file)
 *
 * The component is presentational — the parent owns navigation/state
 * transitions via the onChoice callback.
 */
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Avatar } from '@/components/Avatar';
import { AlertCircle } from 'lucide-react';

export function DuplicateEmailModal({
  open,
  onOpenChange,
  matches = [],
  onChoice,
  onCancel,
}) {
  const primary = matches[0]; // we surface the first match; rare to have > 1
  const activeEval = primary?.evaluations?.find((e) => e.status === 'active');
  const [choice, setChoice] = React.useState('');

  React.useEffect(() => {
    if (open) {
      // Require an explicit choice each time the dialog opens.
      setChoice('');
    }
  }, [open]);

  if (!primary) return null;

  const submit = () => {
    if (!choice) return;

    const payload = {
      action: choice,
      candidate_id: primary.candidate_id,
      evaluation_id: choice === 'open_active' ? activeEval?.id : null,
    };
    onChoice?.(payload);
  };

  const cancel = () => {
    if (onCancel) {
      onCancel();
      return;
    }
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="duplicate-email-dialog" className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-amber-600" />
            Existing candidate found
          </DialogTitle>
          <DialogDescription>
            We already have a candidate with this email. How would you like to proceed?
          </DialogDescription>
        </DialogHeader>

        <div
          className="rounded-lg border bg-secondary/30 p-3"
          data-testid="duplicate-email-matched-candidate"
        >
          <div className="flex items-start gap-3">
            <Avatar name={primary.full_name} size={40} />
            <div className="flex-1">
              <p className="text-sm font-semibold">{primary.full_name}</p>
              <p className="text-xs text-muted-foreground">{primary.email}</p>
              {primary.phone && (
                <p className="text-xs text-muted-foreground">{primary.phone}</p>
              )}
              {primary.evaluations?.length > 0 && (
                <div className="mt-2 space-y-1">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Evaluations on file
                  </p>
                  {primary.evaluations.map((ev) => (
                    <div
                      key={ev.id}
                      className="flex items-center gap-2 text-xs"
                      data-testid={`duplicate-email-eval-${ev.id}`}
                    >
                      <span
                        className={`inline-block h-1.5 w-1.5 rounded-full ${
                          ev.status === 'active' ? 'bg-emerald-500' : 'bg-slate-400'
                        }`}
                      />
                      <span className="font-medium">{ev.name}</span>
                      <span className="text-muted-foreground">
                        {ev.status}
                        {ev.pipeline_name && ` · ${ev.pipeline_name}`}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <RadioGroup
          value={choice}
          onValueChange={setChoice}
          className="space-y-2"
          data-testid="duplicate-email-choice-group"
        >
          <div className="flex items-start gap-2 rounded-md border p-3 hover:bg-accent">
            <RadioGroupItem value="new_eval" id="dup-choice-new" className="mt-0.5" />
            <Label htmlFor="dup-choice-new" className="flex-1 cursor-pointer">
              <span className="text-sm font-medium">
                Start a new evaluation for {primary.full_name}
              </span>
              <p className="text-xs text-muted-foreground">
                Recommended — use this when the candidate is being considered for a new role or round.
              </p>
            </Label>
          </div>
          <div
            className={`flex items-start gap-2 rounded-md border p-3 ${
              activeEval ? 'hover:bg-accent' : 'cursor-not-allowed opacity-50'
            }`}
          >
            <RadioGroupItem
              value="open_active"
              id="dup-choice-active"
              className="mt-0.5"
              disabled={!activeEval}
            />
            <Label
              htmlFor="dup-choice-active"
              className={`flex-1 ${activeEval ? 'cursor-pointer' : 'cursor-not-allowed'}`}
            >
              <span className="text-sm font-medium">
                Open existing active evaluation
                {activeEval ? ` “${activeEval.name}”` : ''}
              </span>
              <p className="text-xs text-muted-foreground">
                {activeEval
                  ? 'Use this if you are updating an in-progress evaluation.'
                  : 'No active evaluation on file — all are closed.'}
              </p>
            </Label>
          </div>
        </RadioGroup>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={cancel}
            data-testid="duplicate-email-cancel-upload-button"
          >
            Cancel Upload
          </Button>
          <Button
            onClick={submit}
            disabled={!choice}
            data-testid="duplicate-email-continue-button"
          >
            Continue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
